/** Sign-up (matrícula por etapas) — Fase 0 data shapes returned by the v2 API. */

export const ENROLLMENT_NOTE_TYPE = {
	ALTA: 1,
	ACTUALIZACION: 2,
	SISTEMA: 3
} as const;

export interface EnrollmentAdvisorDTO {
	/** Zoho CRM user id (string) */
	userId: string;
	name: string;
	email?: string | null;
	sharePercentage: number | null;
}

/** What the UI sends when creating/updating advisors. */
export interface AdvisorSelection {
	userId: string;
	name: string;
	sharePercentage: number | null;
}

export interface AdvisorCandidateDTO {
	id: string;
	name: string;
	email: string | null;
	roles: string[];
}

export interface EnrollmentNoteDTO {
	id: number;
	typeId: number;
	type: string;
	note: string;
	createdAt: string | null;
	createdBy: string | null;
}

export interface EnrollmentAttendanceSummaryDTO {
	total: number;
	present: number;
	absent: number;
	rate: number | null;
}

export interface EnrollmentHistoryDTO {
	id: number;
	campusId: number;
	isCurrentCampus: boolean;
	statusId: number;
	status: string;
	courseId: number | null;
	courseName: string;
	levelId: number | null;
	levelName: string;
	scheduleId: number | null;
	scheduleName: string;
	scheduleCode: string;
	scheduleStatusId: number | null;
	scheduleStatus: string;
	periodName: string;
	startDate: string | null;
	endDate: string | null;
	sessions: string;
	mentorName: string;
	mentorFirstName: string;
	mentorLastName: string;
	classroomName: string;
	buildingName: string;
	createdAt: string | null;
	createdBy: string | null;
	updatedAt: string | null;
	updatedBy: string | null;
	commercialTerms: string | null;
	advisors: EnrollmentAdvisorDTO[];
	notes: EnrollmentNoteDTO[];
	attendance: EnrollmentAttendanceSummaryDTO;
}

export interface AvailableScheduleKitDTO {
	id: number;
	name: string;
	sku: string;
	basePrice: number;
}

export interface AvailableScheduleDTO {
	id: number;
	name: string;
	code: string;
	statusId: number;
	status: string;
	eligibility: "NUEVO" | "REINGRESO";
	courseId: number | null;
	courseName: string;
	levelId: number | null;
	levelName: string;
	periodName: string;
	startDate: string;
	endDate: string;
	billableMonths: number;
	sessions: string;
	mentorName: string;
	mentorFirstName: string;
	mentorLastName: string;
	enrolledStudents: number;
	studentsLimit: number;
	isFull: boolean;
	subscriptionPrice: number | null;
	monthlyBillingPrice: number | null;
	kits: AvailableScheduleKitDTO[];
}

export interface StudentDocumentDTO {
	id: number;
	typeId: number;
	type: string;
	name: string;
	mimeType: string | null;
	size: number | null;
	description: string | null;
	createdAt: string | null;
	createdBy: string | null;
}

export interface StudentDocumentsResponseDTO {
	types: { [typeId: string]: string };
	documents: StudentDocumentDTO[];
}

export interface StudentAuditChangeDTO {
	field: string;
	label: string;
	old: string | null;
	new: string | null;
}

export interface StudentAuditEntryDTO {
	id: number;
	campusId: number | null;
	entityType: "student" | "enrollment" | "document" | string;
	entityId: number | null;
	action: string;
	summary: string;
	changes: StudentAuditChangeDTO[];
	createdAt: string | null;
	createdBy: string;
}

/* ---------- Fase 1: Solicitudes de matrícula ---------- */

export const REQUEST_STATUS = { BORRADOR: 1, ENVIADA: 2, DEVUELTA: 3, APROBADA: 4, CANCELADA: 5 } as const;
export const REQUEST_MODE = { REMOTA: 1, PRESENCIAL: 2 } as const;
export const PAYMENT_INTENT_TYPE = { DEPOSITO: 1, LINK: 2, EN_CAMINO: 3 } as const;
export const PAYMENT_INTENT_STATUS = { PENDIENTE: 1, APLICADO: 2, RECHAZADO: 3, PROCESADO: 4 } as const;
export const REQUEST_DOCUMENT_TYPE = { COMPROBANTE: 1, ID_ALUMNO: 2, ID_TUTOR: 3, OTRO: 4 } as const;

export interface RequestScheduleDTO {
	scheduleId: number; notes: string; name: string; code: string; statusId: number | null;
	courseName: string; levelName: string; periodName: string; startDate: string | null; endDate: string | null;
	sessions: string; subscriptionPrice: number | null; monthlyBillingPrice: number | null;
}
export interface RequestPaymentDTO {
	typeId: number | null;
	notes?: string | null;
	amount: number | null;
	recipientAccountId: number | null;
	paymentMethodId: number | null;
	reference: string | null;
	paidAt: string | null;
}

export interface RequestStudentData {
	firstName?: string; lastName?: string; identificationTypeId?: number | string; identificationCode?: string; birthday?: string;
	genderId?: number | string; nationalityId?: number | string; countryId?: number | string; stateId?: number | string | null; cityId?: number | string | null;
	stateName?: string; cityName?: string; email?: string; phone1?: string; phone2?: string; address?: string; zipcode?: string;
	academicDegreeId?: number | string; lastSchoolName?: string; currentOccupation?: string; observations?: string;
	tagIds?: number[]; contacts?: { name: string; relation: string; contactMedia: string }[]; links?: { facebook?: string; instagram?: string };
}

export interface EnrollmentRequestSummaryDTO {
	id: number; campusId: number; modeId: number; mode: string; statusId: number; status: string;
	/** Quién de Caja tiene el ticket (2026-09-25). */
	cajaShort?: string | null;
	studentId: number | null; isNewStudent: boolean; studentName: string; studentGenderId?: number | null; identificationCode: string | null; phone: string | null;
	courseName: string; levelName: string; scheduleName: string; scheduleId: number | null; scheduleCount?: number;
	advisors: EnrollmentAdvisorDTO[]; advisorsShort?: string[]; paymentTypeId: number | null; paymentAmount: number | null;
	assignedToId: number | null; assignedTo: string | null; assignedToShort?: string | null;
	createdById: number; createdBy: string | null; createdByShort?: string | null;
	createdAt: string | null; updatedAt: string | null; submittedAt: string | null; enrollmentId: number | null; paymentIntentId: number | null;
	/** Devuelta por Caja: el alumno ya está matriculado y solo falta reponer el comprobante. */
	returnedByCaja?: boolean;
}

export interface AttachmentDTO { index: number; name: string; mime: string | null; }
export interface RequestCommentDTO { id: number; typeId: number; body: string; createdAt: string | null; createdBy: string; attachments?: AttachmentDTO[]; }
export interface RequestDocumentDTO { id: number; typeId: number; type: string; name: string; mimeType: string | null; size: number | null; createdAt: string | null; createdBy: string | null;
	comment?: string | null; rejected?: boolean; rejectedAt?: string | null; rejectedBy?: string | null; rejectionReason?: string | null; sentToCaja?: boolean; }
export interface ExistingStudentDTO { id: number; name: string; identityCode: string | null; identificationCode: string | null; phone1: string | null; email: string | null; statusId?: number | null; isActive?: boolean; match?: string;
	/** Su ficha, para confirmarla o corregirla en la solicitud de reingreso (2026-09-25). */
	studentData?: any; }

export interface EnrollmentRequestDetailsDTO extends EnrollmentRequestSummaryDTO {
	studentData: RequestStudentData; notes: string | null; payment: RequestPaymentDTO | {};
	schedules: RequestScheduleDTO[];
	cancelReason: string | null; reviewedBy: string | null; reviewedByShort?: string | null; reviewedAt: string | null;
	schedule: { id: number; name: string; code: string; statusId: number; courseName: string; levelName: string; periodName: string; startDate: string; endDate: string; sessions: string; subscriptionPrice: number | null; monthlyBillingPrice: number | null } | null;
	existingStudent: ExistingStudentDTO | null;
	comments: RequestCommentDTO[]; documents: RequestDocumentDTO[];
	paymentIntent: { id: number; statusId: number; status: string; paymentId: number | null } | null;
	validation: string[]; duplicates: ExistingStudentDTO[];
	permissions: { isRevisor: boolean; isSupervisora: boolean; canManageReviewers: boolean; canPresencial: boolean; canCreate: boolean; canReadAll: boolean;
		isDireccionVentas?: boolean; isAsesoraAdmisiones?: boolean; canEdit: boolean; canSubmit: boolean; canReview: boolean; canResendToCaja?: boolean;
		reassignOptions?: { userId: number; name: string; hint: string }[] };
}

export interface EnrollmentRequestListDTO {
	data: EnrollmentRequestSummaryDTO[]; total: number; perPage: number; currentPage: number; lastPage: number;
	permissions: { isRevisor: boolean; isSupervisora: boolean; canManageReviewers: boolean; canPresencial: boolean; canCreate: boolean; canReadAll: boolean;
		isDireccionVentas?: boolean; isAsesoraAdmisiones?: boolean };
}

export interface RequestCatalogsDTO { statuses: Record<string, string>; modes: Record<string, string>; paymentTypes: Record<string, string>; documentTypes: Record<string, string>; }
export interface ReviewerDTO { userId: number; name: string; email: string | null; active?: boolean; assignedCount?: number; lastAssignedAt?: string | null; }
export interface ReviewersDTO { pool: ReviewerDTO[]; candidates: ReviewerDTO[]; canManage: boolean; }

export interface PaymentIntentSummaryDTO {
	id: number; campusId: number; requestId: number | null; typeId: number; type: string; statusId: number; status: string;
	studentId: number; studentName: string; identityCode: string | null; enrollmentId: number | null; courseName: string; levelName: string; scheduleName: string;
	amount: number | null; recipientAccountId: number | null; paymentMethodId: number | null; reference: string | null; paidAt: string | null;
	hasDocument: boolean; documentName: string | null; documents: IntentDocumentDTO[]; notes: string | null; paymentId: number | null; invoiceId: number | null; resolutionNotes: string | null;
	createdBy: string | null; createdByShort?: string | null; createdAt: string | null; resolvedBy: string | null; resolvedAt: string | null;
	/** A quién de Caja le tocó por turno; quién de Ventas lo mandó y quién de Admisiones lo aprobó (2026-09-25). */
	assignedToShort?: string | null; salesShort?: string | null; admissionsShort?: string | null;
}
export interface IntentDocumentDTO { index: number; name: string; comment?: string | null; uploadedAt?: string | null; uploadedBy?: string | null;
	rejected?: boolean; rejectedAt?: string | null; rejectedBy?: string | null; rejectionReason?: string | null; }
export interface IntentHistoryDTO { type: "creado" | "rechazado" | "reenviado" | "procesado" | string; at: string | null; by: string | null; text: string; docs: string[]; attachments: AttachmentDTO[]; }
export interface PaymentIntentTuitionDTO { id: number; number: number; title: string; typeId: number; statusId: number; subTotal: number; paidAmount: number; pending: number; dueDate: string; }
export interface PaymentIntentDetailsDTO extends PaymentIntentSummaryDTO {
	studentPhone: string | null; history?: IntentHistoryDTO[]; tuitions: PaymentIntentTuitionDTO[]; suggestedTuitionIds: number[]; suggestedTotal: number;
	permissions: { isCaja: boolean; canApply: boolean; canProcess: boolean };
}
export interface PaymentIntentListDTO { data: PaymentIntentSummaryDTO[]; total: number; perPage: number; currentPage: number; lastPage: number; permissions: { isCaja: boolean }; statuses: Record<string, string>; types: Record<string, string>; }
